var _excluded = ["anchorRef", "children", "className", "closeOnClick", "compact", "focusOnOpen", "hoverable", "onClick", "onClose", "onKeyDown", "open", "placement", "pointer", "role"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import PropTypes from "prop-types";
import React, { forwardRef, useEffect, useRef } from "react";
import { keyCodes } from "../../utils/helpers";
import { useCombinedRefs } from "../../utils/hooks";
import AMenuBase from "../AMenuBase";
import { AList } from "../AList";
import "./AMenu.scss";
var AMenu = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var anchorRef = _ref.anchorRef,
    children = _ref.children,
    propsClassName = _ref.className,
    _ref$closeOnClick = _ref.closeOnClick,
    closeOnClick = _ref$closeOnClick === void 0 ? true : _ref$closeOnClick,
    _ref$compact = _ref.compact,
    compact = _ref$compact === void 0 ? false : _ref$compact,
    _ref$focusOnOpen = _ref.focusOnOpen,
    focusOnOpen = _ref$focusOnOpen === void 0 ? true : _ref$focusOnOpen,
    hoverable = _ref.hoverable,
    _onClick = _ref.onClick,
    onClose = _ref.onClose,
    _onKeyDown = _ref.onKeyDown,
    open = _ref.open,
    placement = _ref.placement,
    pointer = _ref.pointer,
    _ref$role = _ref.role,
    role = _ref$role === void 0 ? "menu" : _ref$role,
    rest = _objectWithoutProperties(_ref, _excluded);
  var menuRef = useRef(null);
  var combinedRef = useCombinedRefs(ref, menuRef);
  useEffect(function () {
    if (open && focusOnOpen) {
      setTimeout(function () {
        var _combinedRef$current;
        (_combinedRef$current = combinedRef.current) === null || _combinedRef$current === void 0 ? void 0 : _combinedRef$current.focus();
      }, 0);
    }
  }, [open, combinedRef, focusOnOpen]);
  var getPrevious = function getPrevious() {
    var items = Array.from(combinedRef.current.querySelectorAll(".a-list-item[tabindex]"));
    return items[items.findIndex(function (x) {
      return x.isSameNode(document.activeElement);
    }) - 1] || items[items.length - 1];
  };
  var getNext = function getNext() {
    var items = Array.from(combinedRef.current.querySelectorAll(".a-list-item[tabindex]"));
    return items[items.findIndex(function (x) {
      return x.isSameNode(document.activeElement);
    }) + 1] || items[0];
  };
  var closeHandler = function closeHandler(e) {
    anchorRef.current && anchorRef.current.focus();
    onClose && onClose(e);
  };
  var keyDownHandler = function keyDownHandler(e) {
    if (onClose && e.keyCode === keyCodes.esc) {
      e.preventDefault();
      closeHandler(e);
      anchorRef.current.focus();
    } else if (e.keyCode === keyCodes.tab) {
      closeHandler(e);
      anchorRef.current.focus();
    } else if (e.keyCode === keyCodes.up) {
      e.preventDefault();
      var previous = getPrevious();
      previous && previous.focus();
    } else if (e.keyCode === keyCodes.down) {
      e.preventDefault();
      var next = getNext();
      next && next.focus();
    }
  };
  var className = "a-menu";
  if (pointer) {
    className += " a-menu--pointer";
  }
  if (compact) {
    className += " compact";
  }
  if (propsClassName) {
    className += " ".concat(propsClassName);
  }
  return /*#__PURE__*/React.createElement(AList, _extends({}, rest, {
    component: AMenuBase,
    ref: combinedRef,
    role: role,
    className: className,
    hoverable: hoverable,
    onClick: function onClick(e) {
      closeOnClick && closeHandler(e);
      _onClick && _onClick(e);
    },
    onClose: onClose,
    onKeyDown: function onKeyDown(e) {
      keyDownHandler(e);
      _onKeyDown && _onKeyDown(e);
    },
    open: open,
    placement: placement,
    anchorRef: anchorRef,
    pointer: pointer,
    tabIndex: -1
  }), children);
});
AMenu.propTypes = {
  /**
   * The reference to the menu anchor.
   */
  anchorRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]).isRequired,
  /**
   * Toggles the behavior of closing the menu on click.
   */
  closeOnClick: PropTypes.bool,
  /**
   * Toggles the behavior of focusing the menu on open.
   */
  focusOnOpen: PropTypes.bool,
  /**
   * Toggles the hover visualization on list items.
   */
  hoverable: PropTypes.bool,
  /**
   * Handles the request to close the menu.
   */
  onClose: PropTypes.func,
  /**
   * Toggles the `open` state.
   */
  open: PropTypes.bool,
  /**
   * The placement of the menu.
   */
  placement: PropTypes.oneOf(["top", "top-right", "right-top", "right", "right-bottom", "bottom-right", "bottom", "bottom-left", "left-bottom", "left", "left-top", "top-left"]),
  /**
   * Reduces line-height for menu items
   */
  compact: PropTypes.bool,
  /**
   * Toggles the menu pointer.
   */
  pointer: PropTypes.bool,
  /**
   * Sets the [WAI-ARIA](https://www.w3.org/WAI/standards-guidelines/aria/) role.
   */
  role: PropTypes.string
};
AMenu.displayName = "AMenu";
export default AMenu;